/**
 * 密码强度评估 Hook
 * 评估密码强度并返回详细的标准满足情况
 */

import { useMemo } from "react";

/** 密码强度等级 */
export type PasswordStrength = "weak" | "medium" | "strong";

/** 密码强度颜色 */
export type PasswordStrengthColor = "red" | "yellow" | "green";

/** 密码标准满足情况 */
export interface PasswordCriteria {
  /** 最小长度（至少 6 位） */
  minLength: boolean;
  /** 包含大写字母 */
  hasUppercase: boolean;
  /** 包含小写字母 */
  hasLowercase: boolean;
  /** 包含数字 */
  hasNumber: boolean;
  /** 包含特殊字符 */
  hasSpecialChar: boolean;
}

/** usePasswordStrength 返回值 */
export interface UsePasswordStrengthReturn {
  /** 密码强度等级 */
  strength: PasswordStrength;
  /** 强度分数 (0-100) */
  score: number;
  /** 各项标准满足情况 */
  criteria: PasswordCriteria;
  /** 强度颜色 */
  color: PasswordStrengthColor;
  /** 强度文本 key（用于 i18n） */
  labelKey: string;
}

/** 最小密码长度 */
const MIN_PASSWORD_LENGTH = 6;

/**
 * 评估密码标准满足情况
 * @param password 密码
 * @returns 各项标准满足情况
 */
function evaluateCriteria(password: string): PasswordCriteria {
  return {
    minLength: password.length >= MIN_PASSWORD_LENGTH,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password),
  };
}

/**
 * 计算密码强度分数
 * @param criteria 标准满足情况
 * @param password 密码
 * @returns 分数 (0-100)
 */
function calculateScore(criteria: PasswordCriteria, password: string): number {
  let score = 0;

  // 基础分数：每满足一项标准得 20 分
  if (criteria.minLength) score += 20;
  if (criteria.hasUppercase) score += 20;
  if (criteria.hasLowercase) score += 20;
  if (criteria.hasNumber) score += 20;
  if (criteria.hasSpecialChar) score += 20;

  // 额外加分：密码长度超过最小要求
  if (password.length > MIN_PASSWORD_LENGTH) {
    // 每多一个字符加 2 分，最多加 20 分
    const extraLength = Math.min(password.length - MIN_PASSWORD_LENGTH, 10);
    score += extraLength * 2;
  }

  // 确保分数在 0-100 范围内
  return Math.min(100, Math.max(0, score));
}

/**
 * 根据分数确定强度等级
 * @param score 分数
 * @returns 强度等级
 */
function getStrengthLevel(score: number): PasswordStrength {
  if (score < 40) return "weak";
  if (score < 70) return "medium";
  return "strong";
}

/**
 * 根据强度等级获取颜色
 * @param strength 强度等级
 * @returns 颜色
 */
function getStrengthColor(strength: PasswordStrength): PasswordStrengthColor {
  switch (strength) {
    case "weak":
      return "red";
    case "medium":
      return "yellow";
    case "strong":
      return "green";
  }
}

/**
 * 根据强度等级获取标签 key
 * @param strength 强度等级
 * @returns i18n key
 */
function getStrengthLabelKey(strength: PasswordStrength): string {
  switch (strength) {
    case "weak":
      return "passwordStrength.weak";
    case "medium":
      return "passwordStrength.medium";
    case "strong":
      return "passwordStrength.strong";
  }
}

/**
 * 密码强度评估 Hook
 * @param password 密码
 * @returns 密码强度评估结果
 */
export function usePasswordStrength(
  password: string
): UsePasswordStrengthReturn {
  return useMemo(() => {
    // 空密码返回默认值
    if (!password) {
      return {
        strength: "weak" as PasswordStrength,
        score: 0,
        criteria: {
          minLength: false,
          hasUppercase: false,
          hasLowercase: false,
          hasNumber: false,
          hasSpecialChar: false,
        },
        color: "red" as PasswordStrengthColor,
        labelKey: "passwordStrength.weak",
      };
    }

    const criteria = evaluateCriteria(password);
    const score = calculateScore(criteria, password);
    const strength = getStrengthLevel(score);
    const color = getStrengthColor(strength);
    const labelKey = getStrengthLabelKey(strength);

    return {
      strength,
      score,
      criteria,
      color,
      labelKey,
    };
  }, [password]);
}

/**
 * 纯函数版本的密码强度评估（用于测试）
 * @param password 密码
 * @returns 密码强度评估结果
 */
export function evaluatePasswordStrength(
  password: string
): UsePasswordStrengthReturn {
  if (!password) {
    return {
      strength: "weak",
      score: 0,
      criteria: {
        minLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSpecialChar: false,
      },
      color: "red",
      labelKey: "passwordStrength.weak",
    };
  }

  const criteria = evaluateCriteria(password);
  const score = calculateScore(criteria, password);
  const strength = getStrengthLevel(score);
  const color = getStrengthColor(strength);
  const labelKey = getStrengthLabelKey(strength);

  return {
    strength,
    score,
    criteria,
    color,
    labelKey,
  };
}
